import { Injectable } from '@angular/core';
import { Branding, UserDetails } from 'app/core/auth/user.model';
import { AutopropBackend, WebSocketService } from 'app/shared/backend';
import { map, mergeMap, take } from 'rxjs';
import {
    BillingDetailsResponse,
    BrandingResponse,
    FeatureBundlesResponse,
    NotificationsBundlesResponse,
    ProductsResponse,
    SettingsProfessionsResponse,
    SettingsRegionResponse,
    SettingsResponse,
    SkillsBundlesResponse,
} from './response.model';


@Injectable({
    providedIn: 'root',
})
export class MyAutopropApi {

    constructor(private backend: AutopropBackend, private ws: WebSocketService) {}

    getCredentials() {
        return this.backend.get<SettingsResponse>('/api/user/credentials');
    }

    getFeatureBundles() {
        return this.backend.get<FeatureBundlesResponse>('/api/user/feature-bundles');
    }

    getSkillsBundles() {
        return this.backend.get<SkillsBundlesResponse>('/api/user/skills-bundles');
    }

    getNotificationsBundles() {
        return this.backend.get<NotificationsBundlesResponse>('/api/user/notifications-bundles').pipe(
            map(response => response.payload)
        );
    }

    getProducts() {
        return this.backend.get<ProductsResponse>('/api/user/products');
    }

    setLearnedSkills(skillsLearned: string[]) {
        return this.backend.post('/api/user/set-learned-skills', skillsLearned);
    }

    getBillingDetails() {
        return this.backend.get<BillingDetailsResponse>('/api/user/billing-details');
    }

    updateCreditCard(token: string) {
        return this.backend.post<BillingDetailsResponse>('/api/user/update-credit-card', { token })
            .pipe(map(response => response.payload.billingDetails));
    }

    updateBranding(branding: Partial<Branding>) {
        return this.ws.connection.pipe(
            take(1),
            mergeMap(({ clientId }) => this.backend.post<BrandingResponse>('/api/user/report-branding', { branding, clientId }))
        );
    }

    updateCredentials(payload: any) {
        return this.backend.post('/api/user/save-credentials', payload);
    }

    removeCredentials(credentialsKey: string) {
        return this.backend.post('/api/user/remove-credentials', { credentialsKey });
    }

    getRegions() {
        return this.backend.get<SettingsRegionResponse>('/api/settings/regions').pipe(map(response => response.payload.regions));
    }

    getProfessions() {
        return this.backend.get<SettingsProfessionsResponse>('/api/settings/professions')
            .pipe(map(response => response.payload.professions));
    }

    setRegionAndHomeArea(region: string) {
        return this.backend.post(`/api/user/set-home-area`, { region });
    }

    setProfession(profession: string) {
        return this.backend.post(`/api/user/set-profession`, { profession });
    }

    updateUserInfo(userInfo: Partial<UserDetails & { avatar: string | null }>) {
        return this.backend.post('/api/user/info', userInfo);
    }

    changeUserPassword(oldPassword: string, newPassword: string) {
        return this.backend.post('/api/user/change-password', { oldPassword, newPassword });
    }

    registerUser(userData: any) {
        return this.backend.post('/api/user/register', { ...userData });
    }

    changeUserEmail(newEmail: string) {
        return this.backend.post('/api/user/change-email', { newEmail });
    }

    checkCouponCode(code: string) {
        return this.backend.post('/api/user/check-coupon', { code }).pipe(map(response => response.payload));
    }
}
